import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Calculator, CheckCircle, Clock, DollarSign, AlertCircle,
    TrendingUp, TrendingDown, Minus, Printer, Save, X
} from 'lucide-react';

const DENOMINATIONS = [
    { value: 100, label: '$100' },
    { value: 50, label: '$50' },
    { value: 20, label: '$20' },
    { value: 10, label: '$10' },
    { value: 5, label: '$5' },
    { value: 1, label: '$1' },
    { value: 0.25, label: '25¢' },
    { value: 0.10, label: '10¢' },
    { value: 0.05, label: '5¢' },
    { value: 0.01, label: '1¢' }
];

export default function DailyCloseout() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [closeout, setCloseout] = useState(null);
    const [denominations, setDenominations] = useState({});
    const [notes, setNotes] = useState('');

    useEffect(() => {
        const startCloseout = async () => {
            try {
                const response = await api.post('/closeout/start', {
                    store_id: user?.store_id
                });
                setCloseout(response.data);
                toast.success('Daily closeout started');
            } catch (error) {
                toast.error('Failed to start closeout');
            }
        };

        const loadTodayCloseout = async () => {
            try {
                setLoading(true);
                const response = await api.get('/closeout/today', {
                    params: { store_id: user?.store_id }
                });
                setCloseout(response.data);
                
                // Initialize denominations from existing data if available
                if (response.data.cash_denominations?.length > 0) {
                    const denomMap = {};
                    response.data.cash_denominations.forEach(d => {
                        denomMap[d.denomination] = d.count;
                    });
                    setDenominations(denomMap);
                }
                
                if (response.data.notes) {
                    setNotes(response.data.notes);
                }
            } catch (error) {
                if (error.response?.status === 404) {
                    // No closeout started yet, create one
                    startCloseout();
                } else {
                    toast.error('Failed to load closeout');
                }
            } finally {
                setLoading(false);
            }
        };

        loadTodayCloseout();
    }, [user?.store_id]);

    const handleDenominationChange = (denomination, count) => {
        const numCount = parseInt(count) || 0;
        setDenominations(prev => ({
            ...prev,
            [denomination]: numCount
        }));
    };

    const calculateActualCash = () => {
        return Object.entries(denominations).reduce((total, [denom, count]) => {
            return total + (parseFloat(denom) * count);
        }, 0);
    };

    const calculateOverShort = () => {
        if (!closeout) return 0;
        return calculateActualCash() - closeout.cash_expected;
    };

    const handleSubmit = async () => {
        if (!closeout) return;
        
        const cashActual = calculateActualCash();
        const cashDenominations = Object.entries(denominations)
            .filter(([_, count]) => count > 0)
            .map(([denom, count]) => ({
                denomination: parseFloat(denom),
                count: count,
                total: parseFloat(denom) * count
            }));

        try {
            setSubmitting(true);
            await api.post('/closeout/submit', {
                closeout_id: closeout.id,
                cash_denominations: cashDenominations,
                cash_actual: cashActual,
                notes: notes
            });
            toast.success('Closeout submitted successfully');
            loadTodayCloseout();
        } catch (error) {
            toast.error('Failed to submit closeout');
            console.error('Submit error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            completed: 'bg-green-500/20 text-green-400 border-green-500/30',
            approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        return styles[status] || styles.pending;
    };

    if (loading) {
        return (
            <Layout title="Daily Closeout">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                </div>
            </Layout>
        );
    }

    const actualCash = calculateActualCash();
    const overShort = calculateOverShort();
    const isOverShort = Math.abs(overShort) > 0.01;

    return (
        <Layout title="Daily Closeout">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Daily Closeout</h1>
                        <p className="text-slate-400">End of day reconciliation</p>
                    </div>
                    {closeout && (
                        <Badge variant="outline" className={getStatusBadge(closeout.status)}>
                            {closeout.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                    )}
                </div>

                {closeout && (
                    <>
                        {/* Sales Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card className="bg-card border-border">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Gross Sales</p>
                                            <p className="text-2xl font-bold">
                                                ${closeout.gross_sales?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                        <TrendingUp className="h-8 w-8 text-green-500/50" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-card border-border">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Net Sales</p>
                                            <p className="text-2xl font-bold text-cyan-400">
                                                ${closeout.net_sales?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                        <DollarSign className="h-8 w-8 text-cyan-500/50" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-card border-border">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Transactions</p>
                                            <p className="text-2xl font-bold">
                                                {closeout.total_transactions || 0}
                                            </p>
                                        </div>
                                        <Calculator className="h-8 w-8 text-violet-500/50" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-card border-border">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Avg Ticket</p>
                                            <p className="text-2xl font-bold">
                                                ${closeout.average_ticket?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                        <Minus className="h-8 w-8 text-slate-500/50" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Cash Denominations */}
                            <Card className="bg-card border-border">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <DollarSign className="h-5 w-5 text-cyan-500" />
                                        Cash Count
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {DENOMINATIONS.map(({ value, label }) => (
                                        <div key={value} className="flex items-center gap-4">
                                            <div className="w-16 text-white font-medium">{label}</div>
                                            <div className="flex-1">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={denominations[value] || ''}
                                                    onChange={(e) => handleDenominationChange(value, e.target.value)}
                                                    disabled={closeout.status !== 'pending' && closeout.status !== 'in_progress'}
                                                    className="bg-background border-input text-foreground"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="w-24 text-right text-slate-400">
                                                ${((denominations[value] || 0) * value).toFixed(2)}
                                            </div>
                                        </div>
                                    ))}

                                    <div className="border-t border-slate-700 pt-4 mt-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Expected Cash</span>
                                            <span className="text-white font-medium">
                                                ${closeout.cash_expected?.toFixed(2) || '0.00'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-slate-400">Actual Cash</span>
                                            <span className="text-cyan-400 font-bold text-lg">
                                                ${actualCash.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
                                            <span className="text-slate-400">Over/Short</span>
                                            <span className={`font-bold text-lg ${
                                                overShort > 0 ? 'text-green-400' : 
                                                overShort < 0 ? 'text-red-400' : 'text-slate-400'
                                            }`}>
                                                {overShort > 0 ? '+' : ''}${overShort.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Payment Breakdown */}
                            <div className="space-y-6">
                                <Card className="bg-card border-border">
                                    <CardHeader>
                                        <CardTitle className="text-white">Payment Breakdown</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Cash Sales</span>
                                            <span className="text-white">${closeout.cash_expected?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Card Sales</span>
                                            <span className="text-white">${closeout.card_sales?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Online Orders</span>
                                            <span className="text-white">${closeout.online_orders?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Other</span>
                                            <span className="text-white">${closeout.other_payments?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="bg-card border-border">
                                    <CardHeader>
                                        <CardTitle className="text-white">Adjustments</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Refunds</span>
                                            <span className="text-red-400">-${closeout.refunds_total?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Voids</span>
                                            <span className="text-red-400">-${closeout.voids_total?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Discounts</span>
                                            <span className="text-yellow-400">-${closeout.discounts_total?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Tax Collected</span>
                                            <span className="text-white">${closeout.tax_collected?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Notes */}
                                <Card className="bg-card border-border">
                                    <CardHeader>
                                        <CardTitle className="text-white">Notes</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            disabled={closeout.status !== 'pending' && closeout.status !== 'in_progress'}
                                            className="w-full h-24 bg-background border-input text-foreground rounded-md p-3 resize-none"
                                            placeholder="Add any notes about today's closeout..."
                                        />
                                    </CardContent>
                                </Card>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {(closeout.status === 'pending' || closeout.status === 'in_progress') && (
                            <div className="flex gap-4">
                                <Button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4 mr-2" />
                                            Submit Closeout
                                        </>
                                    )}
                                </Button>
                                
                                {isOverShort && (
                                    <div className="flex items-center gap-2 text-yellow-400 bg-yellow-400/10 px-4 py-2 rounded-md">
                                        <AlertCircle className="h-4 w-4" />
                                        <span className="text-sm">
                                            Cash is {overShort > 0 ? 'over' : 'short'} by ${Math.abs(overShort).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {closeout.status === 'completed' && (
                            <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-3 rounded-md">
                                <CheckCircle className="h-5 w-5" />
                                <span>Closeout submitted and pending manager approval</span>
                            </div>
                        )}

                        {closeout.status === 'approved' && (
                            <div className="flex items-center gap-2 text-emerald-400 bg-emerald-400/10 px-4 py-3 rounded-md">
                                <CheckCircle className="h-5 w-5" />
                                <span>Closeout approved by {closeout.approved_by_name}</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
